import React, { useState, useContext, useEffect, RefAttributes } from "react";
import {
  Grid,
  Container,
  Typography,
  makeStyles,
  Button,
} from "@material-ui/core";
import {
  MUIFormControl,
  muiFieldRenderer,
  muiKeyboardDateField,
} from "@react-typed-form/mui";
import {
  FormGroupState,
  ControlDefs,
  useFormGroup,
} from "@react-typed-form/core";
import { SignUpModelForJolse } from "../gensrc/client";
import { ApiContext } from "./clients";
import { useLocation } from "react-router-dom";
import { BaseUrl } from "./config";
import {
  badRequestToErrors,
  FullWidthTextField,
  ProgressButton,
} from "./utils";
import { ConfirmEmailPrefix } from "./confirm";
import { useGlobalApi } from "./global";
import Alert, { AlertProps } from "@material-ui/lab/Alert";
import { DateTime } from "luxon";

export const SignUpPath = "/signup";

type SignUpForm = Omit<
  SignUpModelForJolse,
  "confirmAccountUrl" | "dateOfBirth"
> & {
  dateOfBirth: DateTime | null;
};

const empty = (v: any) => (!v ? "You must fill this in" : undefined);

const FormGroupSignUp: ControlDefs<SignUpForm, MUIFormControl> = {
  email: {
    label: "Email",
    autoComplete: "email",
    required: true,
    fullWidth: true,
    validator: empty,
  },
  password: {
    label: "Password",
    required: true,
    type: "password",
    fullWidth: true,
    autoComplete: "new-password",
    // validator: (v) =>
    //   v.length < 6
    //     ? "Password too short, must be 6 characters"
    //     : !PassRegex.test(v)
    //     ? "Must contain at least one digit"
    //     : undefined,
  },
  confirmPassword: {
    label: "Confirm Password",
    required: true,
    type: "password",
    fullWidth: true,
    autoComplete: "new-password",
    // validator: (v, ctx) =>
    //   v !== ctx.password ? "Password does not match" : undefined,
  },
  firstName: {
    label: "First Name",
    required: true,
    fullWidth: true,
    validator: empty,
  },
  lastName: {
    label: "Last Name",
    required: true,
    fullWidth: true,
    validator: empty,
  },
  mobileNumber: {
    label: "Mobile number",
    autoComplete: "tel",
    required: true,
    fullWidth: true,
    validator: empty,
  },
  dateOfBirth: {
    label: "Date of birth",
    required: true,
    fullWidth: true,
    validator: empty,
  },
};

const useStyles = makeStyles((t) => ({
  fields: {
    margin: t.spacing(1),
  },
}));

export function SignUpPage() {
  const classes = useStyles();
  const apiClients = useContext(ApiContext);
  const globalApi = useGlobalApi();
  const location = useLocation();
  const [valid, setValid] = useState(false);
  const [signingUp, setSigningUp] = useState(false);
  const { state, FormField, updateState, useFieldRenderer } = useFormGroup(
    FormGroupSignUp,
    {
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      dateOfBirth: null,
    },
    muiFieldRenderer,
    (state) => setValid(state.valid)
  );
  const DateField = useFieldRenderer(muiKeyboardDateField);

  globalApi.useHideNav();

  function createAccount() {
    setSigningUp(true);
    const formValue = state.current.value;
    apiClients.authClient
      .createUser({
        ...formValue,
        confirmAccountUrl: BaseUrl + ConfirmEmailPrefix,
        dateOfBirth: formValue.dateOfBirth!.toISODate(),
      })
      .then((a) => {
        globalApi.loggedIn(a, false);
        globalApi.addSnack(<Alert severity="success">Account created</Alert>);
        globalApi.backOrHome(location);
      })
      .catch((e) => {
        const errors = badRequestToErrors<SignUpForm>(e);
        setSigningUp(false);
        updateState({ dirty: true, touched: true }, errors);
      });
  }

  function showForm() {
    return (
      <>
        <Typography variant="h4" align="center" className={classes.fields}>
          Create Account
        </Typography>
        <Grid container spacing={3} className={classes.fields}>
          <Grid item xs={10}>
            <FormField field="email" />
          </Grid>
          <Grid item xs={10}>
            <FormField field="password" />
          </Grid>
          <Grid item xs={10}>
            <FormField field="confirmPassword" />
          </Grid>
          <Grid item xs={10}>
            <FormField field="firstName" />
          </Grid>
          <Grid item xs={10}>
            <FormField field="lastName" />
          </Grid>
          <Grid item xs={10}>
            <FormField field="mobileNumber" />
          </Grid>
          <Grid item xs={10}>
            <DateField
              field="dateOfBirth"
              variant="inline"
              format="dd/MM/yyyy"
              TextFieldComponent={FullWidthTextField}
            />
          </Grid>
          <Grid item xs={10}>
            <ProgressButton loading={signingUp}>
              <Button
                variant="contained"
                disabled={!valid || signingUp}
                color="primary"
                fullWidth
                onClick={() => createAccount()}
              >
                Sign Up
              </Button>
            </ProgressButton>
          </Grid>
        </Grid>
      </>
    );
  }

  return <Container maxWidth="xs">{showForm()}</Container>;
}
